import { useRef } from "react";
import { gsap, ScrollTrigger, prefersReducedMotion } from "@/lib/gsapSetup";
import { useEffect } from "react";
import { process } from "@/data/siteContent";
import Img from "@/components/Img";
import SectionHeading from "@/components/SectionHeading";

function Chapter({ chapter }) {
  const ref = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ref.current.querySelector("[data-clip]"),
        { clipPath: "inset(100% 0% 0% 0%)" },
        {
          clipPath: "inset(0% 0% 0% 0%)",
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: { trigger: ref.current, start: "top 80%" },
        }
      );
      gsap.from(ref.current.querySelectorAll("[data-line]"), {
        y: 40,
        opacity: 0,
        duration: 0.9,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: { trigger: ref.current, start: "top 82%" },
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={ref}
      className="grid grid-cols-1 items-center gap-8 border-t border-white/10 py-12 md:grid-cols-12 md:gap-12 md:py-16"
      data-testid={`process-chapter-${chapter.no}`}
    >
      <div className="md:col-span-2">
        <span
          data-line
          className="font-accent text-6xl italic text-white/25 md:text-7xl"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {chapter.no}
        </span>
      </div>
      <div className="md:col-span-5">
        <h3
          data-line
          className="font-display text-3xl tracking-tight text-white md:text-5xl"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {chapter.title}
        </h3>
        <p data-line className="mt-5 max-w-md font-body text-base leading-[1.7] text-white/70">
          {chapter.body}
        </p>
      </div>
      <div className="md:col-span-5">
        <div className="overflow-hidden rounded-2xl">
          <Img
            data-clip
            src={chapter.image}
            alt={chapter.title}
            width={1400}
            height={720}
            sizes="(max-width: 768px) 100vw, 42vw"
            className="h-64 w-full object-cover grayscale transition-all duration-700 hover:grayscale-0 md:h-72"
          />
        </div>
      </div>
    </div>
  );
}

export default function Process() {
  return (
    <section
      id="process"
      className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32"
      data-testid="process-section"
    >
      <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <SectionHeading
          eyebrow={process.eyebrow}
          title={process.heading}
          titleClassName="max-w-xl lg:text-6xl"
        />
      </div>

      <div>
        {process.chapters.map((c) => (
          <Chapter key={c.no} chapter={c} />
        ))}
      </div>
    </section>
  );
}
